import { DomainError, type IsoDate } from "./model.ts";

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

export function parseIsoDate(value: IsoDate): number {
  const match = ISO_DATE.exec(value);
  if (!match) throw new DomainError("INVALID_DATE", `Malformed ISO date: ${value}`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const instant = Date.UTC(year, month - 1, day);
  const roundTrip = new Date(instant);
  if (
    roundTrip.getUTCFullYear() !== year ||
    roundTrip.getUTCMonth() !== month - 1 ||
    roundTrip.getUTCDate() !== day
  ) {
    throw new DomainError("INVALID_DATE", `Not a calendar date: ${value}`);
  }
  return instant;
}

export function isIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== "string") return false;
  try {
    parseIsoDate(value);
    return true;
  } catch {
    return false;
  }
}

function formatUtc(instant: number): IsoDate {
  const date = new Date(instant);
  return [
    String(date.getUTCFullYear()).padStart(4, "0"),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

export function addDays(date: IsoDate, amount: number): IsoDate {
  return formatUtc(parseIsoDate(date) + Math.trunc(amount) * DAY_MS);
}

export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((parseIsoDate(to) - parseIsoDate(from)) / DAY_MS);
}

export function weekday(date: IsoDate): number {
  return new Date(parseIsoDate(date)).getUTCDay();
}

export function weekStart(date: IsoDate): IsoDate {
  return addDays(date, -((weekday(date) + 6) % 7));
}

export function monthStart(date: IsoDate): IsoDate {
  const parsed = new Date(parseIsoDate(date));
  return formatUtc(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), 1));
}

export function monthEnd(date: IsoDate): IsoDate {
  const parsed = new Date(parseIsoDate(date));
  return formatUtc(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth() + 1, 0));
}

export function datesInclusive(start: IsoDate, end: IsoDate): IsoDate[] {
  const count = daysBetween(start, end);
  if (count < 0) return [];
  return Array.from({ length: count + 1 }, (_, index) => addDays(start, index));
}
